import gql from 'graphql-tag';

const appointmentsNode = `
  nodes {
    id
    start
    end
    duration
    classroomId
    liveByLiveId {
      id
      name
      sourceType
      sourceId
      providerAccountId
      avatarUrl
    }
  }
`;

const userByUserId = `
  userByUserId {
    id
    name
    role
  }
`;

const contentNodes = `
  nodes {
    id
    articleId
    questionId
    assertionId
    solutionId
    commentId
    position
    textByTextId {
      id
      body
      alignment
      source
    }
    imageByImageId {
      id
      url
      source
      resizeFactor
      description
    }
    videoByVideoId {
      id
      providerId
      providerType
    }
  }
`;

const solutionsByQuestionId = `
  solutionsByQuestionId {
    nodes {
      id
      updatedAt
      questionId
      ${userByUserId}
      contentsBySolutionId {
        ${contentNodes}
      }
    }
  }
`;

const exerciseCommentAttachmentsByCommentId = `
  exerciseCommentAttachmentsByCommentId {
    nodes {
      id
      userId
      commentId
      fileName
      fileSize
      fileType
      fileUrl
    }
  }
`;

const revisionListInfo = `
  id
  name
  slug
  type
  createdAt
  listItemsByListId {
    nodes {
      id
      listId
      videoId
      articleId
      position
      questionByQuestionId {
        id
        answered
        assertionsByQuestionId {
          nodes {
            id
            questionId
          }
        }
      }
    }
  }
  repliesByListId(condition: { userId: $userId }) {
    nodes {
      id
      listId
      questionId
      userId
      assertionId
    }
  }
`;

const listInfoBase = `
  id
  name
  slug
  listItemsByListId {
    nodes {
      id
      listId
      videoId
      articleId
      position
      questionByQuestionId {
        id
        mode
      }
    }
  }
`;

const boardsClassroomsByClassroomId = `
  boardsClassroomsByClassroomId {
    nodes {
      id
      classroomId

      boardByBoardId {
        id
        title
        workload
        isPreregistered
        hasPostgradMicrocertificateEnabled
      }
    }
  }
`;

export const dexUpdateUserMutation = gql`
  mutation updateUser($userId: Int!, $name: String) {
    updateUserById(input: { id: $userId, userPatch: { name: $name } }) {
      user {
        name
        email
      }
    }
  }
`;

export const allListSessionsQuery = gql`
  query allListSessionsQuery($userId: Int, $listIds: [Int!]) {
    allListSessions(filter: { userId: { equalTo: $userId }, listId: { in: $listIds } }) {
      totalCount
      nodes {
        id
        userId
        listId
        totalWrong
        totalCorrect
      }
    }
  }
`;

export const appointmentsByClassroomIds = gql`
  query appointmentsByClassroomIds($classroomIds: [Int!], $startDate: Datetime!) {
    allAppointments(filter: {
      classroomId: { in: $classroomIds }
      end: { greaterThan: $startDate }
    }) {
      ${appointmentsNode}
    }
  }
`;

export const userInfoByEmail = gql`
  query userInfoByEmail($userEmail: String!) {
    userByEmail(email: $userEmail) {
      id
      name
      email
      role
      contractsByUserId(
        filter: { courseByCourseId: { descomplicaCategorySlug: { equalTo: "pos" } } }
      ) {
        nodes {
          id
          courseByCourseId {
            id
            name
            slug
            descomplicaCourseId
            descomplicaCategorySlug
          }
          createdAt
          expiresAt
        }
      }
    }
  }
`;

export const userStartedEnrollments = gql`
    query userStartedEnrollments($userEmail: String!, $currentCourseId: Int) {
      userByEmail(email: $userEmail) {
        id
        startedEnrollments(
          filter: {
            classroomByClassroomId: {
              or: [
                { courseId: { equalTo: $currentCourseId } },
                { courseId: { isNull: true } }
              ]
            }
          } ) {
          nodes {
            id
            userId
            createdAt
            classroomByClassroomId {
              id
              name
              slug
              type
              courseId
              stoppedLists(first: 1) {
                nodes {
                  ${listInfoBase}
                }
              }
              associatedLists(first: 1) {
                nodes {
                  ${listInfoBase}
                }
              }
              ${boardsClassroomsByClassroomId}
            }
          }
        }
      }
    }
  `;

export const revisionListsByClassroomSlug = gql`
  query revisionListByClassroomSlug($classroomSlug: String!, $userId: Int!) {
    classroomBySlug(slug: $classroomSlug) {
      id
      name
      slug
      type
      progress
      materialsByClassroomId(filter: {
        listByListId: {
          type: { like: "REVIEW_LIST" }
        }
      }) {
        nodes {
          id
          listByListId {
            ${revisionListInfo}
          }
        }
      }
    }
  }
`;

export const classroomMaterialsByClassroomId = gql`
  query classroomMaterialsByClassroomId($classroomId: Int!) {
    classroomById(id: $classroomId) {
      id
      boardsClassroomsByClassroomId {
        nodes {
          id
          classroomId
          boardByBoardId {
            id
            title
            materialsByBoardId {
              nodes {
                id
                boardId
                position
                listByListId {
                  id
                  name
                  slug
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const classroomBoardSectionsByClassroomId = gql`
  query classroomBoardSectionsByClassroomId($classroomId: Int!, $userId: Int!) {
    classroomById(id: $classroomId) {
      id
      boardsClassroomsByClassroomId {
        nodes {
          id
          classroomId
          boardByBoardId {
            id
            title
            boardSectionsByBoardId {
              nodes {
                id
                title
                slug
                position
                materialsByBoardSectionsId {
                  totalCount
                  nodes {
                    id
                    position
                    boardSectionsId
                    listByListId {
                      id
                      name
                      slug
                      listItemsByListId {
                        nodes {
                          id
                          listId
                          videoId
                          articleId
                          position
                          questionByQuestionId {
                            id
                            mode
                            answered
                            commentsByQuestionId(filter: { userId: { equalTo: $userId } }) {
                              nodes {
                                id
                                userId
                                questionId
                                createdAt
                                score
                                userByUserId {
                                  id
                                  name
                                  role
                                }
                                contentsByCommentId {
                                  nodes {
                                    id
                                    articleId
                                    questionId
                                    assertionId
                                    solutionId
                                    commentId
                                    position
                                    textByTextId {
                                      id
                                      body
                                      alignment
                                      source
                                    }
                                    imageByImageId {
                                      id
                                      url
                                      source
                                      resizeFactor
                                      description
                                    }
                                    videoByVideoId {
                                      id
                                      providerId
                                      providerType
                                    }
                                  }
                                }
                              }
                            }
                            assertionsByQuestionId {
                              nodes {
                                id
                                questionId
                              }
                            }
                          }
                          progressesByListItemId(filter: { userId: { equalTo: $userId } }) {
                            nodes {
                              id
                              listItemId
                              createdAt
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const classroomReadingsByClassroomId = gql`
  query classroomReadingsByClassroomId($classroomId: Int!) {
    classroomById(id: $classroomId) {
      id
      boardsClassroomsByClassroomId {
        nodes {
          id
          classroomId
          boardByBoardId {
            id
            title
            readingsByBoardId {
              nodes {
                id
                name
                type
                priority
              }
            }
          }
        }
      }
    }
  }
`;

export const classroomAppointmentsByClassroomId = gql`
  query classroomAppointmentsByClassroomId($classroomId: Int!) {
    classroomById(id: $classroomId) {
      id
      appointmentsByClassroomId {
        ${appointmentsNode}
      }
    }
  }
`;

export const classroomInfoQuery = gql`
  query classroomInfoQuery($userId: Int!, $classroomSlug: String!) {
    allClassrooms(
      filter: {
        and: [
          { slug: { equalTo: $classroomSlug } }
          { enrollmentsByClassroomId: { some: { userId: { equalTo: $userId } } } }
        ]
      }
    ) {
      nodes {
        name
        id
        slug
        type
        progress
        enrollmentsByClassroomId {
          nodes {
            userId
          }
        }
      }
    }
  }
`;

export const revisionListBySlug = gql`
  query revisionListBySlug($listSlug: String!) {
    listBySlug(slug: $listSlug) {
      id
      name
      slug
      listItemsByListId {
        nodes {
          id
          listId
          position
          questionByQuestionId {
            id
            mode
            alternatives
            contentsByQuestionId {
              ${contentNodes}
            }
            assertionsByQuestionId {
              nodes {
                id
                questionId
                position
                contentsByAssertionId {
                  ${contentNodes}
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const listBySlug = gql`
  query listBySlug($listSlug: String!, $userId: Int!) {
    listBySlug(slug: $listSlug) {
      id
      name
      slug
      progress
      repliesByListId(condition: { userId: $userId }) {
        nodes {
          id
          listId
          questionId
          userId
          assertionId
        }
      }
      listItemsByListId {
        nodes {
          id
          listId
          position
          progressesByListItemId(filter: { userId: { equalTo: $userId } }) {
            nodes {
              id
              listItemId
              createdAt
            }
          }
          videoByVideoId {
            id
            name
            duration
            providerId
            providerType
          }
          articleByArticleId {
            id
            title
            contentsByArticleId {
              ${contentNodes}
            }
          }
          questionByQuestionId {
            id
            mode
            alternatives
            answered
            contentsByQuestionId {
              ${contentNodes}
            }
            assertionsByQuestionId {
              nodes {
                id
                questionId
                position
                contentsByAssertionId {
                  ${contentNodes}
                }
              }
            }
            commentsByQuestionId(filter: { userId: { equalTo: $userId } }) {
              nodes {
                id
                userId
                questionId
                createdAt
                updatedAt
                score
                attemptCount
                ${userByUserId}
                ${exerciseCommentAttachmentsByCommentId}
                contentsByCommentId {
                  ${contentNodes}
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const createReplyMutation = gql`
  mutation createReply(
    $listId: Int!
    $questionId: Int!
    $userId: Int!
    $assertionId: Int!
    $answer: JSON
  ) {
    createReply(
      input: {
        reply: {
          listId: $listId
          questionId: $questionId
          userId: $userId
          assertionId: $assertionId
          answer: $answer
        }
      }
    ) {
      reply {
        id
        questionId
        userId
        assertionId
        listByListId {
          id
          name
          slug
          progress
        }
        questionByQuestionId {
          ${solutionsByQuestionId}
        }
      }
    }
  }
`;

export const CreateProgressMutation = gql`
  mutation CreateProgress($listItemId: Int!, $userId: Int!) {
    createProgress(input: { progress: { listItemId: $listItemId, userId: $userId } }) {
      progress {
        id
        listItemId
        userId
      }
    }
  }
`;

export const ProgressByUserIdAndListItemId = gql`
  query ProgressByUserIdAndListItemId($listItemId: Int!, $userId: Int!) {
    progressByUserIdAndListItemId(listItemId: $listItemId, userId: $userId) {
      id
      listItemId
      userId
    }
  }
`;

export const createUserThinkAndAnswerComment = gql`
  mutation createUserThinkAndAnswerComment($questionId: Int!, $text: String!) {
    createUserThinkAndAnswerComment(questionId: $questionId, text: $text) {
      success
      message
      comment {
        id
        userId
        questionId
        contentsByCommentId
      }
      content {
        id
        textId
        commentId
      }
      text {
        id
        body
      }
    }
  }
`;

export const createNestedCommentTextAndContent = gql`
  mutation createNestedCommentTextAndContent($questionId: Int!, $userId: Int!, $text: String, $attemptCount: Int) {
    createNestedCommentTextAndContent(
      input: { comment: { questionId: $questionId, userId: $userId, attemptCount: $attemptCount }, text: { body: $text, userId: $userId } }
    ) {
      nestedCommentTextAndContent {
        comment {
          id
          updatedAt
          userId
          questionId
          attemptCount
          contentsByCommentId {
            ${contentNodes}
          }
        }
      }
    }
  }
`;

export const createUserDiscursiveExerciseComment = gql`
  mutation createUserExerciseComment(
    $questionId: Int!
    $body: String!
    $attemptCount: Int
    $attachments: [UserExerciseAttachments]
  ) {
    createUserExerciseComment(
      questionId: $questionId
      body: $body
      attemptCount: $attemptCount
      attachments: $attachments
    ) {
      comment {
        id
        userId
        questionId
        contentsByCommentId
        exerciseCommentAttachmentsByCommentId
        createdAt
        updatedAt
        attemptCount
      }
      content {
        id
        userId
        textId
        commentId
      }
      text {
        id
        userId
        body
      }
      exerciseCommentAttachments {
        id
        userId
        commentId
        fileName
        fileSize
        fileUrl
        fileType
      }
    }
  }
`;

export const updateUserPracticeActivityComment = gql`
  mutation updateUserPracticeActivityComment(
    $textId: Int!
    $questionId: Int!
    $commentId: Int!
    $body: String!
    $attachments: [UserExerciseAttachments]
  ) {
    updateUserPracticeActivityComment(
      textId: $textId
      questionId: $questionId
      commentId: $commentId
      body: $body
      attachments: $attachments
    ) {
      success
      message
      comment {
        id
        userId
        questionId
        attemptCount
        updatedAt
        exerciseCommentAttachmentsByCommentId
      }
      text {
        id
        userId
        body
      }
      exerciseCommentAttachments {
        id
        userId
        commentId
        fileName
        fileSize
        fileUrl
        fileType
      }
    }
  }
`;

export const getForumParticipantsInfoQuery = gql`
  query getForumParticipants($userIdList: [Int!]) {
    allUsers(filter: { id: { in: $userIdList } }) {
      nodes {
        id
        name
      }
    }
  }
`;

export const updateCommentById = gql`
  mutation updateCommentById($commentId: Int!, $score: Float) {
    updateCommentById(input: { id: $commentId, commentPatch: { score: $score } }) {
      comment {
        id
        score
      }
    }
  }
`;

export const getListsProgressQuery = gql`
  query getListsProgress($listIds: [Int!]) {
    getListsProgress(listIds: $listIds) {
      listId
      progress
      stats {
        video {
          current
          max
        }
        article {
          current
          max
        }
        question {
          current
          max
        }
        trivia {
          current
          max
        }
        practice {
          current
          max
        }
      }
    }
  }
`;

export const getClassroomsProgress = gql`
  query getClassroomsProgress($classroomIds: [Int!]) {
    getClassroomsProgress(classroomIds: $classroomIds) {
      classroomId
      progress
    }
  }
`;

export const getExamGroupDetails = gql`
  query getDurationAndQuestionsCount($dexExamGroupId: Int!) {
    examGroupById(id: $dexExamGroupId) {
      examGroupsExamsByExamGroupId {
        nodes {
          examByExamId {
            duration
            examQuestionsByExamId {
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const listsAnsweredSolutionsQuery = gql`
  query listsAnsweredSolutions($listSlug: String!) {
    listBySlug(slug: $listSlug) {
      id
      name
      slug
      listItemsByListId(filter: { questionByQuestionId: { answered: { equalTo: true } } }) {
        nodes {
          questionByQuestionId {
            id
            mode
            answered
            ${solutionsByQuestionId}
          }
        }
      }
    }
  }
`;

export const syncUserCoursesMutation = gql`
  mutation syncUserCourses($userToken: String!) {
    syncUserCoursesBetweenChimeraAndDex(userChimeraToken: $userToken)
  }
`;

export const getListInfoById = gql`
  query listByIdStudyPlan($listId: Int!) {
    listById(id: $listId) {
      id
      name
      slug
    }
  }
`;

export const getUserFirstProgress = gql`
  query getUserFirstProgress($userId: Int!) {
    userById(id: $userId) {
      progressesByUserId(first: 1, orderBy: ID_ASC) {
        nodes {
          id
          createdAt
        }
      }
    }
  }
`;
